import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Fuel as FuelIcon, Plus, Upload, Pencil, Trash2, Paperclip, X, Zap, Gauge, DollarSign, Route, Loader2,
  ChevronDown, Search, FileSpreadsheet, FileText,
} from 'lucide-react';
import {
  rowsToCsv, downloadBlob, newLandscapeDoc, addBrandHeader, addFooters, loadLogoPng, drawPdfTable,
} from '../../utils/tripExport';

const pad = (n) => String(n).padStart(2, '0');
const localDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const periodRange = (p) => {
  const now = new Date();
  const tomorrow = new Date(now); tomorrow.setDate(now.getDate() + 1);
  if (p === 'this_month') return { start: localDate(new Date(now.getFullYear(), now.getMonth(), 1)), end: localDate(new Date(now.getFullYear(), now.getMonth() + 1, 1)) };
  if (p === 'last_month') return { start: localDate(new Date(now.getFullYear(), now.getMonth() - 1, 1)), end: localDate(new Date(now.getFullYear(), now.getMonth(), 1)) };
  if (p === 'last_90') { const s = new Date(now); s.setDate(now.getDate() - 90); return { start: localDate(s), end: localDate(tomorrow) }; }
  if (p === 'this_year') return { start: `${now.getFullYear()}-01-01`, end: localDate(tomorrow) };
  const s = new Date(now); s.setDate(now.getDate() - 30);
  return { start: localDate(s), end: localDate(tomorrow) };
};

const PERIODS = [
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'last_30', label: 'Last 30 Days' },
  { value: 'last_90', label: 'Last 90 Days' },
  { value: 'this_year', label: 'This Year' },
];

const SOURCE_BADGE = {
  sensor: { label: 'Sensor', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400' },
  estimate: { label: 'Estimate', cls: 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400' },
  off: { label: 'Manual', cls: 'bg-muted text-muted-foreground' },
};

const PRICE_FIELDS = [
  { key: 'petrol', label: 'Petrol ($/L)' },
  { key: 'diesel', label: 'Diesel ($/L)' },
  { key: 'lpg', label: 'LPG ($/L)' },
  { key: 'hybrid', label: 'Hybrid ($/L)' },
  { key: 'electric', label: 'Electric ($/kWh)' },
];

const money = (v) => (v == null ? '—' : `$${Number(v).toFixed(2)}`);
const fmtSize = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const emptyForm = { device_id: '', entry_date: localDate(new Date()), quantity: '', quantity_unit: 'L', unit_price: '', total_cost: '', odometer: '', station: '', notes: '', attachments: [] };

const StatCard = ({ icon: Icon, label, value, sub, testId }) => (
  <Card data-testid={testId}>
    <CardContent className="p-4">
      <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium"><Icon className="w-3.5 h-3.5" /> {label}</div>
      <p className="text-2xl font-semibold mt-1">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
    </CardContent>
  </Card>
);

export const FuelPage = ({ api }) => {
  const [period, setPeriod] = useState('this_month');
  const [summary, setSummary] = useState(null);
  const [entries, setEntries] = useState([]);
  const [entriesTotal, setEntriesTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [pendingRemoveAtt, setPendingRemoveAtt] = useState(null);
  const [attachmentsFor, setAttachmentsFor] = useState(null);
  const [vehicleFilter, setVehicleFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [searchQ, setSearchQ] = useState('');
  const [exporting, setExporting] = useState(false);
  const [pricesOpen, setPricesOpen] = useState(false);
  const [prices, setPrices] = useState({});
  const [priceDraft, setPriceDraft] = useState({});
  const [savingPrices, setSavingPrices] = useState(false);
  const sessionUploads = useRef([]);
  const searchTimer = useRef(null);

  const range = useMemo(() => periodRange(period), [period]);
  const logQuery = useCallback((skip = 0) => {
    const parts = [`start=${range.start}`, `end=${range.end}`, `skip=${skip}`];
    if (vehicleFilter !== 'all') parts.push(`device_id=${vehicleFilter}`);
    if (searchQ) parts.push(`q=${encodeURIComponent(searchQ)}`);
    return parts.join('&');
  }, [range.start, range.end, vehicleFilter, searchQ]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sumRes, entRes] = await Promise.all([
        api.get(`/fleet/fuel/summary?start=${range.start}&end=${range.end}`),
        api.get(`/fleet/fuel/entries?${logQuery()}&limit=50`),
      ]);
      setSummary(sumRes.data);
      setPrices(sumRes.data.prices || {});
      setEntries(entRes.data.entries || []);
      setEntriesTotal(entRes.data.total || 0);
    } catch (e) {
      toast.error('Failed to load fuel data');
    } finally {
      setLoading(false);
    }
  }, [api, range.start, range.end, logQuery]);

  useEffect(() => { load(); }, [load]);

  const onSearchChange = (v) => {
    setSearch(v);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setSearchQ(v.trim()), 400);
  };

  const loadMore = async () => {
    try {
      const res = await api.get(`/fleet/fuel/entries?${logQuery(entries.length)}&limit=50`);
      setEntries((prev) => [...prev, ...(res.data.entries || [])]);
    } catch { toast.error('Failed to load more entries'); }
  };

  const allVehicles = summary?.all_vehicles || [];
  const selectedVehicle = allVehicles.find((v) => v.id === form.device_id);
  const isEV = selectedVehicle?.fuel_type === 'electric';
  const isHybrid = selectedVehicle?.fuel_type === 'hybrid';
  const unitLabel = form.quantity_unit === 'kWh' ? 'kWh' : 'L';

  const openAdd = () => { setEditingEntry(null); setForm(emptyForm); sessionUploads.current = []; setDialogOpen(true); };
  const openEdit = (e) => {
    setEditingEntry(e);
    setForm({ device_id: e.device_id, entry_date: e.entry_date, quantity: e.quantity ?? '', quantity_unit: e.quantity_unit || 'L', unit_price: e.unit_price ?? '', total_cost: e.total_cost ?? '', odometer: e.odometer ?? '', station: e.station || '', notes: e.notes || '', attachments: [...(e.attachments || [])] });
    sessionUploads.current = [];
    setDialogOpen(true);
  };

  // Files uploaded in this dialog session but never saved to an entry would be orphans — clean them up on cancel
  const closeEntryDialog = () => {
    const orphans = [...sessionUploads.current];
    sessionUploads.current = [];
    orphans.forEach((id) => { api.delete(`/service/attachments/${id}`).catch(() => {}); });
    setDialogOpen(false);
  };

  const setF = (k, v) => setForm((prev) => {
    const next = { ...prev, [k]: v };
    const q = parseFloat(k === 'quantity' ? v : next.quantity);
    if (k === 'quantity' || k === 'unit_price') {
      const up = parseFloat(k === 'unit_price' ? v : next.unit_price);
      if (q > 0 && up >= 0 && !Number.isNaN(up)) next.total_cost = (q * up).toFixed(2);
    } else if (k === 'total_cost') {
      const tc = parseFloat(v);
      if (q > 0 && tc >= 0 && !Number.isNaN(tc)) next.unit_price = (tc / q).toFixed(3);
    }
    return next;
  });

  // Same storage + flow as the Maintenance Log Service dialog (/api/service/upload)
  const uploadAttachment = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    let ok = 0;
    for (const f of Array.from(files)) {
      try {
        const fd = new FormData();
        fd.append('file', f);
        const res = await api.post('/service/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        const { id, url, name, size, content_type } = res.data;
        sessionUploads.current.push(id);
        setForm((prev) => ({ ...prev, attachments: [...(prev.attachments || []), { id, url, name, size, content_type }] }));
        ok += 1;
      } catch (e) {
        toast.error(e?.response?.data?.detail || `Upload failed: ${f.name}`);
      }
    }
    setUploading(false);
    if (ok) toast.success(ok === 1 ? 'Attachment uploaded' : `${ok} attachments uploaded`);
  };

  const confirmRemoveAttachment = async () => {
    const att = pendingRemoveAtt;
    if (!att) return;
    try {
      if (att.id) await api.delete(`/service/attachments/${att.id}`);
      sessionUploads.current = sessionUploads.current.filter((id) => id !== att.id);
      setForm((prev) => ({ ...prev, attachments: (prev.attachments || []).filter((a) => (a.id || a.url) !== (att.id || att.url)) }));
      setPendingRemoveAtt(null);
      toast.success('Attachment removed');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to remove attachment'); }
  };

  const openAttachment = async (att) => {
    const win = window.open('', '_blank');
    try {
      const r = await api.get(att.url.replace(/^\/api/, ''), { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(r.data);
      if (win) win.location = blobUrl; else window.open(blobUrl, '_blank');
    } catch (e) {
      if (win) win.close();
      toast.error(e?.response?.status === 404 ? 'Attachment file not found' : 'Could not open attachment');
    }
  };

  const handleSave = async () => {
    if (!form.device_id) { toast.error('Select a vehicle'); return; }
    const qty = parseFloat(form.quantity);
    if (!qty || qty <= 0) { toast.error('Quantity must be greater than 0'); return; }
    setSaving(true);
    try {
      const payload = {
        device_id: form.device_id,
        entry_date: form.entry_date,
        quantity: qty,
        quantity_unit: form.quantity_unit,
        unit_price: form.unit_price === '' ? null : parseFloat(form.unit_price),
        total_cost: form.total_cost === '' ? null : parseFloat(form.total_cost),
        odometer: form.odometer === '' ? null : parseFloat(form.odometer),
        station: form.station || null,
        notes: form.notes || null,
        attachments: form.attachments || [],
      };
      if (editingEntry) await api.put(`/fleet/fuel/entries/${editingEntry.id}`, payload);
      else await api.post('/fleet/fuel/entries', payload);
      sessionUploads.current = [];
      toast.success(editingEntry ? 'Fill-up updated' : 'Fill-up added');
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save fill-up');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const r = await api.delete(`/fleet/fuel/entries/${deleteTarget.id}`);
      toast.success(r.data?.attachments_removed ? `Fill-up deleted (${r.data.attachments_removed} file(s) removed from storage)` : 'Fill-up deleted');
      setDeleteTarget(null);
      load();
    } catch { toast.error('Failed to delete fill-up'); }
  };

  const fetchAllEntries = async () => {
    let all = [];
    let total = 1;
    while (all.length < total) {
      const res = await api.get(`/fleet/fuel/entries?${logQuery(all.length)}&limit=200`);
      total = res.data.total || 0;
      const batch = res.data.entries || [];
      if (!batch.length) break;
      all = [...all, ...batch];
    }
    return all;
  };

  const exportLog = async (fmt) => {
    setExporting(true);
    try {
      const endIncl = new Date(range.end); endIncl.setDate(endIncl.getDate() - 1);
      const fname = `fuel-log-${range.start}-to-${localDate(endIncl)}`;
      if (fmt === 'csv') {
        const res = await api.get(`/fleet/fuel/entries/export?${logQuery()}`, { responseType: 'blob' });
        downloadBlob(res.data, `${fname}.csv`, 'text/csv;charset=utf-8;');
        toast.success('CSV exported');
      } else {
        const rows = await fetchAllEntries();
        if (!rows.length) { toast.error('No fill-ups to export'); return; }
        const headers = ['Date', 'Vehicle', 'Qty', 'Unit Price', 'Total', 'Odometer (km)', 'Station', 'Notes'];
        const data = rows.map((e) => [
          e.entry_date, e.vehicle_name, `${e.quantity} ${e.quantity_unit}`,
          e.unit_price != null ? `$${Number(e.unit_price).toFixed(3)}` : '-',
          e.total_cost != null ? `$${Number(e.total_cost).toFixed(2)}` : '-',
          e.odometer != null ? String(e.odometer) : '-', e.station || '-', e.notes || '-',
        ]);
        const logo = await loadLogoPng();
        const { doc, layout } = newLandscapeDoc();
        const weights = [10, 18, 9, 9, 9, 10, 15, 20];
        const y = addBrandHeader(doc, layout, logo, 'Fuel Log', `${range.start} to ${localDate(endIncl)} • ${rows.length} fill-up${rows.length === 1 ? '' : 's'}`);
        drawPdfTable(doc, layout, y, headers.map((label, i) => ({ label, weight: weights[i] })), data);
        addFooters(doc, layout);
        doc.save(`${fname}.pdf`);
        toast.success('PDF exported');
      }
    } catch { toast.error('Export failed'); }
    finally { setExporting(false); }
  };

  const savePrices = async () => {
    setSavingPrices(true);
    try {
      const res = await api.put('/fleet/fuel/settings', { prices: priceDraft });
      setPrices(res.data.prices || {});
      toast.success('Fuel prices saved');
      setPricesOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save fuel prices');
    } finally { setSavingPrices(false); }
  };

  const openPrices = () => { setPriceDraft({ ...prices }); setPricesOpen(true); };

  const fleet = summary?.fleet || {};
  const vehicles = summary?.vehicles || [];

  return (
    <div className="space-y-4" data-testid="fuel-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <FuelIcon className="w-5 h-5 text-foreground" />
            <h2 className="text-xl font-semibold">Fuel Management</h2>
          </div>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">
            Fill-ups, consumption and cost per vehicle. Sensor readings use the mapped Fuel Level / EV Battery Level IO types.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[150px] h-9" data-testid="fuel-period-select"><SelectValue /></SelectTrigger>
            <SelectContent>{PERIODS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="outline" size="sm" className="h-9" onClick={openPrices} data-testid="fuel-prices-btn">
            <DollarSign className="w-4 h-4 mr-1" /> Fuel Prices
          </Button>
          <Button size="sm" className="h-9 bg-[#2563eb] hover:bg-[#1d4ed8]" onClick={openAdd} data-testid="fuel-add-entry-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Fill-Up
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={DollarSign} label="Total Spend" value={money(fleet.spend)} sub={fleet.estimated_cost > 0 ? `+ ${money(fleet.estimated_cost)} estimated` : null} testId="fuel-stat-spend" />
        <StatCard icon={FuelIcon} label="Volume" value={`${(fleet.litres || 0).toFixed(1)} L`} sub={fleet.kwh > 0 ? `+ ${fleet.kwh.toFixed(1)} kWh` : null} testId="fuel-stat-volume" />
        <StatCard icon={Route} label="Distance" value={`${(fleet.distance_km || 0).toLocaleString()} km`} sub="moving distance (drift excluded)" testId="fuel-stat-distance" />
        <StatCard icon={Gauge} label="Avg Cost / km" value={fleet.cost_per_km != null ? `$${fleet.cost_per_km.toFixed(3)}` : '—'} sub={`${fleet.entries_count || 0} fill-ups`} testId="fuel-stat-costkm" />
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Vehicles</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
          ) : vehicles.length === 0 ? (
            <div className="flex flex-col items-center py-10 text-center">
              <FuelIcon className="w-10 h-10 text-muted-foreground opacity-40 mb-2" />
              <p className="text-sm text-muted-foreground font-medium">No fuel activity in this period</p>
              <p className="text-xs text-muted-foreground mt-1">Add a fill-up, or set a vehicle&apos;s Fuel Source (Admin Panel &gt; Device Edit) to Sensor or Estimate.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="fuel-vehicles-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Vehicle</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Level</TableHead>
                    <TableHead className="text-right">Fill-ups</TableHead>
                    <TableHead className="text-right">Volume</TableHead>
                    <TableHead className="text-right">Cost</TableHead>
                    <TableHead className="text-right">Distance</TableHead>
                    <TableHead className="text-right">Consumption</TableHead>
                    <TableHead className="text-right">Cost/km</TableHead>
                    <TableHead className="text-right">Est. Cost</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {vehicles.map((v) => {
                    const badge = SOURCE_BADGE[v.fuel_source] || SOURCE_BADGE.off;
                    const unit = v.fuel_type === 'electric' ? 'kWh/100km' : 'L/100km';
                    return (
                      <TableRow key={v.id} data-testid={`fuel-vehicle-row-${v.id}`}>
                        <TableCell className="font-medium whitespace-nowrap">{v.name}{v.fuel_type === 'electric' && <Zap className="w-3 h-3 inline ml-1 text-emerald-500" />}</TableCell>
                        <TableCell><Badge variant="outline" className={`text-[10px] ${badge.cls}`}>{badge.label}</Badge></TableCell>
                        <TableCell className="whitespace-nowrap">{v.fuel_level ? `${v.fuel_level.value}${v.fuel_level.units || ''}` : '—'}</TableCell>
                        <TableCell className="text-right">{v.entries_count || '—'}</TableCell>
                        <TableCell className="text-right whitespace-nowrap">{v.litres > 0 ? `${v.litres} L` : v.kwh > 0 ? `${v.kwh} kWh` : '—'}</TableCell>
                        <TableCell className="text-right">{v.cost > 0 ? money(v.cost) : '—'}</TableCell>
                        <TableCell className="text-right whitespace-nowrap">{v.distance_km > 0 ? `${v.distance_km} km` : '—'}</TableCell>
                        <TableCell className="text-right whitespace-nowrap" title="Actual consumption needs at least 2 fill-ups (fuel used between fills ÷ distance between them)">
                          {v.consumption_actual != null ? `${v.consumption_actual} ${unit}` : '—'}
                          {v.reference_consumption != null && <span className="text-xs text-muted-foreground"> / ref {v.reference_consumption}</span>}
                        </TableCell>
                        <TableCell className="text-right">{v.cost_per_km != null ? `$${v.cost_per_km.toFixed(3)}` : '—'}</TableCell>
                        <TableCell className="text-right whitespace-nowrap">{v.estimated_cost != null ? `${money(v.estimated_cost)} (${v.estimated_quantity} ${v.fuel_type === 'electric' ? 'kWh' : 'L'})` : '—'}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="text-base">Fill-Up Log</CardTitle>
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input className="h-8 w-[220px] pl-8 text-xs" placeholder="Search vehicle / station…"
                  value={search} onChange={(e) => onSearchChange(e.target.value)} data-testid="fuel-log-search-input" />
              </div>
              <Select value={vehicleFilter} onValueChange={setVehicleFilter}>
                <SelectTrigger className="w-[170px] h-8 text-xs" data-testid="fuel-log-vehicle-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All vehicles</SelectItem>
                  {allVehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="h-8" disabled={exporting} data-testid="fuel-export-dropdown-trigger">
                    {exporting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />} Export <ChevronDown className="w-3.5 h-3.5 ml-1 opacity-70" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuLabel className="text-[10px] text-muted-foreground pt-1 pb-0.5">Fill-Up Log</DropdownMenuLabel>
                  <DropdownMenuItem onSelect={() => exportLog('csv')} data-testid="fuel-export-csv">
                    <FileSpreadsheet className="w-4 h-4 mr-2" /> CSV (.csv)
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => exportLog('pdf')} data-testid="fuel-export-pdf">
                    <FileText className="w-4 h-4 mr-2 text-[#2563eb]" /> PDF (.pdf)
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {entries.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">{searchQ ? 'No fill-ups match your search.' : 'No fill-ups recorded in this period.'}</p>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="fuel-entries-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Vehicle</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead className="text-right">Unit Price</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="text-right">Odometer</TableHead>
                    <TableHead>Station</TableHead>
                    <TableHead>Files</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entries.map((e) => (
                    <TableRow key={e.id} data-testid={`fuel-entry-row-${e.id}`}>
                      <TableCell className="whitespace-nowrap">{e.entry_date}</TableCell>
                      <TableCell className="font-medium whitespace-nowrap">{e.vehicle_name}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">{e.quantity} {e.quantity_unit}</TableCell>
                      <TableCell className="text-right">{e.unit_price != null ? `$${Number(e.unit_price).toFixed(3)}` : '—'}</TableCell>
                      <TableCell className="text-right font-medium">{money(e.total_cost)}</TableCell>
                      <TableCell className="text-right">{e.odometer != null ? `${e.odometer.toLocaleString()} km` : '—'}</TableCell>
                      <TableCell className="max-w-[160px] truncate">{e.station || '—'}</TableCell>
                      <TableCell>
                        {(e.attachments || []).length > 0 ? (
                          <Button size="sm" variant="outline" className="h-7 px-2 text-[11px] gap-1" onClick={() => setAttachmentsFor(e)} title="View attachments" data-testid={`fuel-entry-attachments-${e.id}`}>
                            <Paperclip className="w-3 h-3" /> {e.attachments.length}
                          </Button>
                        ) : <span className="text-muted-foreground text-xs">—</span>}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(e)} data-testid={`fuel-entry-edit-${e.id}`}>
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setDeleteTarget(e)} data-testid={`fuel-entry-delete-${e.id}`}>
                            <Trash2 className="w-3.5 h-3.5 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {entries.length < entriesTotal && (
                <div className="flex justify-center mt-3">
                  <Button variant="outline" size="sm" onClick={loadMore} data-testid="fuel-entries-load-more">
                    Load more ({entries.length} of {entriesTotal})
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Add / Edit Fill-Up (styling consistent with Maintenance Log Service) ── */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { if (!o) { if (uploading) return; closeEntryDialog(); } else setDialogOpen(o); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="fuel-entry-dialog">
          <DialogHeader><DialogTitle>{editingEntry ? 'Edit Fill-Up' : 'Add Fill-Up'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Vehicle</Label>
                <Select value={form.device_id} onValueChange={(v) => {
                  const veh = allVehicles.find((x) => x.id === v);
                  setForm((prev) => ({ ...prev, device_id: v, quantity_unit: veh?.fuel_type === 'electric' ? 'kWh' : 'L' }));
                }}>
                  <SelectTrigger data-testid="fuel-form-vehicle-select"><SelectValue placeholder="Select vehicle" /></SelectTrigger>
                  <SelectContent>
                    {allVehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Date</Label>
                <Input type="date" value={form.entry_date} onChange={(e) => setF('entry_date', e.target.value)} data-testid="fuel-form-date-input" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Quantity ({unitLabel})</Label>
                <Input type="number" min="0" step="0.01" value={form.quantity} onChange={(e) => setF('quantity', e.target.value)} placeholder="0.00" data-testid="fuel-form-quantity-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Price / {unitLabel}</Label>
                <Input type="number" min="0" step="0.001" value={form.unit_price} onChange={(e) => setF('unit_price', e.target.value)} placeholder="0.000" data-testid="fuel-form-unit-price-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Total ($)</Label>
                <Input type="number" min="0" step="0.01" value={form.total_cost} onChange={(e) => setF('total_cost', e.target.value)} placeholder="0.00" data-testid="fuel-form-total-input" />
              </div>
            </div>
            {(isHybrid || isEV) && (
              <div className="space-y-1">
                <Label className="text-xs">Unit</Label>
                <Select value={form.quantity_unit} onValueChange={(v) => setF('quantity_unit', v)}>
                  <SelectTrigger className="w-[120px]" data-testid="fuel-form-unit-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="L">Litres</SelectItem>
                    <SelectItem value="kWh">kWh</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Odometer (km)</Label>
                <Input type="number" min="0" value={form.odometer} onChange={(e) => setF('odometer', e.target.value)} placeholder="Optional" data-testid="fuel-form-odometer-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Station</Label>
                <Input value={form.station} onChange={(e) => setF('station', e.target.value)} placeholder="Optional" data-testid="fuel-form-station-input" />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setF('notes', e.target.value)} placeholder="Optional" data-testid="fuel-form-notes-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Attachments (receipts / invoices / photos)</Label>
              <div className="flex items-center gap-2">
                <input type="file" id="fuel-file" className="hidden" multiple accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.csv,.txt" onChange={(e) => { uploadAttachment(e.target.files); e.target.value = ''; }} data-testid="fuel-form-file-input" />
                <Button type="button" variant="outline" size="sm" onClick={() => document.getElementById('fuel-file').click()} disabled={uploading} data-testid="fuel-form-upload-btn">
                  {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />} Add files
                </Button>
                <span className="text-[11px] text-muted-foreground">{(form.attachments || []).length ? `${form.attachments.length} attached` : 'No files yet'}</span>
              </div>
              {(form.attachments || []).length > 0 && (
                <ul className="mt-1 divide-y rounded-md border text-xs" data-testid="fuel-form-attachment-list">
                  {form.attachments.map((a, i) => (
                    <li key={a.id || a.url || i} className="flex items-center gap-2 px-2 py-1.5">
                      <Paperclip className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                      <button type="button" className="flex-1 min-w-0 truncate text-left text-blue-600 dark:text-blue-400 hover:underline" onClick={() => openAttachment(a)} data-testid={`fuel-form-attachment-${i}`}>{a.name || 'Attachment'}</button>
                      {a.size != null && <span className="text-muted-foreground flex-shrink-0">{fmtSize(a.size)}</span>}
                      <Button type="button" size="icon" variant="ghost" className="h-6 w-6 text-red-500 flex-shrink-0" onClick={() => setPendingRemoveAtt(a)} title="Remove (deletes the file)" data-testid={`fuel-form-attachment-remove-${i}`}><X className="w-3 h-3" /></Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={closeEntryDialog} data-testid="fuel-form-cancel-btn">Cancel</Button>
            <Button size="sm" className="bg-[#2563eb] hover:bg-[#1d4ed8]" onClick={handleSave} disabled={saving || uploading} data-testid="fuel-form-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}{editingEntry ? 'Save Changes' : 'Add Fill-Up'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Remove attachment confirm (same flow as Log Service) ── */}
      <Dialog open={!!pendingRemoveAtt} onOpenChange={(o) => { if (!o) setPendingRemoveAtt(null); }}>
        <DialogContent className="max-w-sm" data-testid="fuel-attachment-remove-dialog">
          <DialogHeader><DialogTitle>Remove Attachment</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Remove <span className="font-medium text-foreground">{pendingRemoveAtt?.name || 'this file'}</span>? The file is permanently deleted from storage.
          </p>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setPendingRemoveAtt(null)} data-testid="fuel-attachment-remove-cancel">Cancel</Button>
            <Button variant="destructive" size="sm" onClick={confirmRemoveAttachment} data-testid="fuel-attachment-remove-confirm">Remove</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Attachments viewer (same as Maintenance) ── */}
      <Dialog open={!!attachmentsFor} onOpenChange={(o) => { if (!o) setAttachmentsFor(null); }}>
        <DialogContent className="max-w-md" data-testid="fuel-attachments-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Paperclip className="w-4 h-4" /> Attachments</DialogTitle>
          </DialogHeader>
          <ul className="divide-y rounded-md border text-sm">
            {(attachmentsFor?.attachments || []).map((a, i) => (
              <li key={a.id || i} className="flex items-center gap-2 px-3 py-2">
                <Paperclip className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                <button type="button" className="flex-1 min-w-0 truncate text-left text-blue-600 dark:text-blue-400 hover:underline" onClick={() => openAttachment(a)} data-testid={`fuel-attachment-open-${i}`}>{a.name || 'Attachment'}</button>
                {a.size != null && <span className="text-xs text-muted-foreground flex-shrink-0">{fmtSize(a.size)}</span>}
              </li>
            ))}
          </ul>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setAttachmentsFor(null)} data-testid="fuel-attachments-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Per-account Fuel Prices ── */}
      <Dialog open={pricesOpen} onOpenChange={setPricesOpen}>
        <DialogContent className="max-w-md" data-testid="fuel-prices-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><DollarSign className="w-4 h-4" /> Fuel Prices</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground -mt-2">
            Your account&apos;s default price per fuel type, used by the estimate engine when a vehicle has no price override.
          </p>
          <div className="grid grid-cols-2 gap-3">
            {PRICE_FIELDS.map((f) => (
              <div key={f.key} className="space-y-1">
                <Label className="text-xs">{f.label}</Label>
                <Input type="number" min="0" step="0.001" className="h-8"
                  value={priceDraft[f.key] ?? ''}
                  onChange={(e) => setPriceDraft((prev) => ({ ...prev, [f.key]: e.target.value }))}
                  data-testid={`fuel-price-input-${f.key}`} />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setPricesOpen(false)} data-testid="fuel-prices-cancel-btn">Cancel</Button>
            <Button size="sm" className="bg-[#2563eb] hover:bg-[#1d4ed8]" onClick={savePrices} disabled={savingPrices} data-testid="fuel-prices-save-btn">
              {savingPrices && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />}Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <DialogContent className="max-w-sm" data-testid="fuel-delete-dialog">
          <DialogHeader><DialogTitle>Delete Fill-Up</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Delete the {deleteTarget?.entry_date} fill-up for {deleteTarget?.vehicle_name}?
            {(deleteTarget?.attachments || []).length > 0 && ` Its ${deleteTarget.attachments.length} attached file(s) will be permanently removed from storage.`} This cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setDeleteTarget(null)} data-testid="fuel-delete-cancel-btn">Cancel</Button>
            <Button variant="destructive" size="sm" onClick={handleDelete} data-testid="fuel-delete-confirm-btn">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FuelPage;
